<template>
    <div class="space-y-6">
        <div v-for="(section, sIndex) in sections" :key="section.id" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div class="mb-6">
                <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                    {{ sIndex + 1 }}. {{ section.title || 'Untitled Section' }}
                </h2>
                <p v-if="section.description" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {{ section.description }}
                </p>
            </div>

            <div class="space-y-6">
                <div v-for="(field, fIndex) in getVisibleFields(section)" :key="field.id" class="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
                    <!-- Field Label -->
                    <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                        {{ sIndex + 1 }}.{{ fIndex + 1 }} {{ field.label || 'Untitled Field' }}
                        <span v-if="isFieldRequired(field)" class="text-red-500 ml-1">*</span>
                    </label>
                    <p v-if="field.help_text" class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        {{ field.help_text }}
                    </p>

                    <!-- Field Type Display (Static) -->
                    <div class="text-sm text-gray-600 dark:text-gray-400">
                        <!-- Yes/No -->
                        <div v-if="field.field_type === 'yes_no'" class="flex space-x-3">
                            <span class="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">Yes</span>
                            <span class="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">No</span>
                        </div>

                        <!-- Pass/Fail/NA -->
                        <div v-else-if="field.field_type === 'pass_fail_na'" class="flex space-x-3">
                            <span class="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md">Pass</span>
                            <span class="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md">Fail</span>
                            <span class="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-400 rounded-md">N/A</span>
                        </div>

                        <!-- Multiple Choice -->
                        <div v-else-if="field.field_type === 'multiple_choice'" class="space-y-2">
                            <div v-for="(option, idx) in getFieldOptions(field)" :key="idx" class="flex items-center">
                                <span class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 mr-2"></span>
                                <span class="text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
                            </div>
                        </div>

                        <!-- Multiple Selection (Checkboxes) -->
                        <div v-else-if="field.field_type === 'multiple_selection'" class="space-y-2">
                            <div v-for="(option, idx) in getFieldOptions(field)" :key="idx" class="flex items-center">
                                <span class="w-4 h-4 rounded border-2 border-gray-300 dark:border-gray-600 mr-2"></span>
                                <span class="text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
                            </div>
                        </div>

                        <!-- Dropdown -->
                        <div v-else-if="field.field_type === 'dropdown_select'" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">
                            <span class="text-gray-500 dark:text-gray-400">Select an option...</span>
                        </div>

                        <!-- Short Text -->
                        <div v-else-if="field.field_type === 'text_input'" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">
                            <span class="text-gray-500 dark:text-gray-400">Text input...</span>
                        </div>

                        <!-- Long Text -->
                        <div v-else-if="field.field_type === 'textarea'" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md min-h-[80px]">
                            <span class="text-gray-500 dark:text-gray-400">Text area...</span>
                        </div>

                        <!-- Number -->
                        <div v-else-if="field.field_type === 'number_input'" class="flex items-center space-x-2">
                            <div class="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">
                                <span class="text-gray-500 dark:text-gray-400">0</span>
                            </div>
                            <span v-if="getFieldConfig(field)?.unit" class="text-sm text-gray-600 dark:text-gray-400">
                                {{ getFieldConfig(field).unit }}
                            </span>
                        </div>

                        <!-- Decimal -->
                        <div v-else-if="field.field_type === 'decimal_input'" class="flex items-center space-x-2">
                            <div class="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">
                                <span class="text-gray-500 dark:text-gray-400">0.00</span>
                            </div>
                            <span v-if="getFieldConfig(field)?.unit" class="text-sm text-gray-600 dark:text-gray-400">
                                {{ getFieldConfig(field).unit }}
                            </span>
                        </div>

                        <!-- Rating -->
                        <div v-else-if="field.field_type === 'rating_scale'" class="flex space-x-1">
                            <span v-for="i in (getFieldConfig(field)?.max || 5)" :key="i">
                                <svg class="w-8 h-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                            </span>
                        </div>

                        <!-- Date -->
                        <div v-else-if="field.field_type === 'date_input'" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">
                            <span class="text-gray-500 dark:text-gray-400">Select date...</span>
                        </div>

                        <!-- Time -->
                        <div v-else-if="field.field_type === 'time_input'" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-md">
                            <span class="text-gray-500 dark:text-gray-400">Select time...</span>
                        </div>

                        <!-- Photo Capture -->
                        <div v-else-if="field.field_type === 'photo_capture'" class="text-gray-500 dark:text-gray-400 italic">
                            Photo capture field
                        </div>

                        <!-- File Upload -->
                        <div v-else-if="field.field_type === 'file_upload'" class="text-gray-500 dark:text-gray-400 italic">
                            File upload field
                        </div>

                        <!-- Signature -->
                        <div v-else-if="field.field_type === 'signature'" class="text-gray-500 dark:text-gray-400 italic">
                            Signature field
                        </div>

                        <!-- Barcode/QR Scan -->
                        <div v-else-if="field.field_type === 'barcode_scan'" class="text-gray-500 dark:text-gray-400 italic">
                            Barcode/QR scan field
                        </div>

                        <!-- Asset Selection -->
                        <div v-else-if="field.field_type === 'asset_selection'" class="text-gray-500 dark:text-gray-400 italic">
                            Asset selection field
                        </div>

                        <!-- Location Selection -->
                        <div v-else-if="field.field_type === 'location_selection'" class="text-gray-500 dark:text-gray-400 italic">
                            Location selection field
                        </div>

                        <!-- Information Display -->
                        <div v-else-if="field.field_type === 'information_display'" class="text-sm text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                            {{ getFieldConfig(field)?.content || 'Information text will appear here' }}
                        </div>

                        <!-- Section Divider -->
                        <div v-else-if="field.field_type === 'section_divider'" class="border-t-2 border-gray-300 dark:border-gray-600 my-4"></div>

                        <!-- Unknown Field Type -->
                        <div v-else class="text-gray-500 dark:text-gray-400 italic">
                            {{ field.field_type }} field
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
const props = defineProps({
    sections: {
        type: Array,
        default: () => []
    }
});

const getVisibleFields = (section) => {
    if (!section.fields) return [];
    return section.fields.filter(field => field.is_active !== false);
};

const isFieldRequired = (field) => {
    return field.is_required === true || field.is_required === 1;
};

const getFieldOptions = (field) => {
    try {
        const config = typeof field.field_config === 'string'
            ? JSON.parse(field.field_config)
            : field.field_config;
        return config?.options || [];
    } catch (e) {
        return [];
    }
};

const getFieldConfig = (field) => {
    try {
        return typeof field.field_config === 'string'
            ? JSON.parse(field.field_config)
            : field.field_config;
    } catch (e) {
        return {};
    }
};
</script>
